import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

export default function ValuesAudit() {
  const { user } = useAuth();
  const [dimensions, setDimensions] = useState([]);
  const [ratings, setRatings] = useState({});
  const [reflections, setReflections] = useState({});
  const [overall, setOverall] = useState('');
  const [pastAudits, setPastAudits] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/values/dimensions'),
      api.get('/values/audits'),
    ]).then(([dimRes, auditRes]) => {
      setDimensions(dimRes.data);
      setPastAudits(auditRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/values/audits', { ratings, reflections, overall_reflection: overall });
      setSubmitted(true);
      const r = await api.get('/values/audits');
      setPastAudits(r.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-center py-20 fade-in">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#ECFDF5' }}>
          <Check size={32} style={{ color: '#2D8B5E' }} />
        </div>
        <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>Values Audit Submitted</h2>
        <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Thank you for reflecting. Your honesty makes the system stronger.</p>
        <button onClick={() => setSubmitted(false)} className="mt-4 px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Submit Another</button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 fade-in" data-testid="values-audit">
      <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E1DC', borderTopWidth: '3px', borderTopColor: '#1B2A4A' }}>
        <h2 className="text-lg font-bold mb-1" style={{ color: '#1B2A4A' }}>Values Self-Audit</h2>
        <p className="text-sm mb-6" style={{ color: '#6B7280', fontFamily: 'Georgia, serif' }}>
          Rate yourself honestly on each value. This isn't a performance review — it's a mirror. The only wrong answer is the one you polish to look good.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {dimensions.map(dim => (
            <div key={dim.key} className="p-4 rounded-lg" style={{ backgroundColor: '#F7F5F2' }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{dim.label}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#6B7280', fontFamily: 'Georgia, serif' }}>{dim.prompt}</p>
                </div>
              </div>

              {/* Rating 1-10 */}
              <div className="flex items-center gap-1 mt-3 mb-2">
                <span className="text-[10px] mr-1" style={{ color: '#9CA3AF' }}>1</span>
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button key={n} type="button" onClick={() => setRatings(p => ({...p, [dim.key]: n}))}
                    className="w-7 h-7 rounded-md text-xs font-medium transition-all"
                    style={{
                      backgroundColor: ratings[dim.key] === n ? (n >= 7 ? '#2D8B5E' : n >= 4 ? '#BA7517' : '#D4726A') : 'white',
                      color: ratings[dim.key] === n ? 'white' : '#6B7280',
                      border: `1px solid ${ratings[dim.key] === n ? 'transparent' : '#E5E1DC'}`,
                    }}
                    data-testid={`rating-${dim.key}-${n}`}>
                    {n}
                  </button>
                ))}
                <span className="text-[10px] ml-1" style={{ color: '#9CA3AF' }}>10</span>
              </div>

              {/* Reflection */}
              <textarea
                value={reflections[dim.key] || ''}
                onChange={e => setReflections(p => ({...p, [dim.key]: e.target.value}))}
                placeholder="Your honest reflection..."
                rows={2}
                className="w-full px-3 py-2 rounded-md border text-sm resize-none mt-1"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                data-testid={`reflection-${dim.key}`}
              />
            </div>
          ))}

          {/* Overall */}
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: '#1B2A4A' }}>Overall Reflection</label>
            <textarea
              value={overall}
              onChange={e => setOverall(e.target.value)}
              placeholder="Looking at these ratings as a whole — what story do they tell? What's the one thing you want to change?"
              rows={3}
              className="w-full px-3 py-2 rounded-md border text-sm resize-none"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A', fontFamily: 'Georgia, serif' }}
              data-testid="overall-reflection"
            />
          </div>

          <button type="submit" disabled={submitting} className="w-full py-3 rounded-md text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#1B2A4A' }} data-testid="submit-values-audit">
            {submitting ? 'Submitting...' : 'Submit Values Audit'}
          </button>
        </form>
      </div>

      {/* Past Audits */}
      {pastAudits.length > 0 && (
        <div className="bg-white rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
          <button onClick={() => setShowPast(!showPast)} className="w-full flex items-center justify-between px-5 py-3 text-left">
            <span className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Previous Audits ({pastAudits.length})</span>
            {showPast ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showPast && (
            <div className="px-5 pb-4 space-y-2">
              {pastAudits.map(a => (
                <div key={a.id} className="p-3 rounded-md" style={{ backgroundColor: '#F7F5F2' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{a.quarter} {a.year}</span>
                    <span className="text-xs" style={{ color: '#9CA3AF' }}>{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString('en-IN') : ''}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(a.ratings || {}).map(([key, val]) => {
                      const dim = dimensions.find(d => d.key === key);
                      return (
                        <span key={key} className="text-xs px-2 py-0.5 rounded-full" style={{
                          backgroundColor: val >= 7 ? '#ECFDF5' : val >= 4 ? '#FFFBEB' : '#FEF2F2',
                          color: val >= 7 ? '#2D8B5E' : val >= 4 ? '#BA7517' : '#D4726A',
                        }}>{dim?.label || key}: {val}/10</span>
                      );
                    })}
                  </div>
                  {a.overall_reflection && <p className="text-xs mt-2 italic" style={{ color: '#6B7280' }}>{a.overall_reflection}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
